import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Leaf, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  FileText,
  Briefcase,
  HelpCircle,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegisterPremiumPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { login } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    birth_date: '',
    residence: '',
    fiscal_code: '',
    profession: '',
    consultation_reason: '',
    how_found_us: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const howFoundUsOptions = language === 'it' 
    ? ['Google', 'Social Media', 'Amici/Familiari', 'Medico/Specialista', 'Altro']
    : ['Google', 'Social Media', 'Friends/Family', 'Doctor/Specialist', 'Other'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = language === 'it' ? 'Nome richiesto' : 'Name required';
    if (!formData.surname.trim()) newErrors.surname = language === 'it' ? 'Cognome richiesto' : 'Surname required';
    if (!formData.email.trim()) newErrors.email = language === 'it' ? 'Email richiesta' : 'Email required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = language === 'it' ? 'Email non valida' : 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = language === 'it' ? 'Telefono richiesto' : 'Phone required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.birth_date) {
      newErrors.birth_date = language === 'it' ? 'Data di nascita richiesta' : 'Birth date required';
    } else {
      // Check if user is 18+
      const birthDate = new Date(formData.birth_date);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < 18) {
        newErrors.birth_date = language === 'it' 
          ? 'Devi avere almeno 18 anni per accedere al servizio' 
          : 'You must be at least 18 years old to access the service';
      }
    }
    if (!formData.residence.trim()) newErrors.residence = language === 'it' ? 'Residenza richiesta' : 'Residence required';
    if (!formData.fiscal_code.trim()) newErrors.fiscal_code = language === 'it' ? 'Codice fiscale richiesto' : 'Fiscal code required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.password) newErrors.password = language === 'it' ? 'Password richiesta' : 'Password required';
    if (formData.password.length < 6) newErrors.password = language === 'it' ? 'Minimo 6 caratteri' : 'Minimum 6 characters';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = language === 'it' ? 'Le password non corrispondono' : 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register-premium`, {
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        phone: formData.phone,
        birth_date: formData.birth_date,
        residence: formData.residence,
        fiscal_code: formData.fiscal_code,
        profession: formData.profession,
        consultation_reason: formData.consultation_reason,
        how_found_us: formData.how_found_us,
        password: formData.password
      });

      // Store token and redirect to pricing for payment
      localStorage.setItem('token', response.data.access_token);
      toast.success(language === 'it' ? 'Registrazione completata!' : 'Registration complete!');
      navigate('/pricing');
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.detail || (language === 'it' ? 'Errore nella registrazione' : 'Registration error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">{language === 'it' ? 'Nome' : 'First Name'} *</Label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={language === 'it' ? 'Il tuo nome' : 'Your first name'}
              className={`pl-12 h-12 rounded-xl bg-secondary/30 border-0 ${errors.name ? 'ring-2 ring-destructive' : ''}`}
              data-testid="premium-name-input"
            />
          </div>
          {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="surname">{language === 'it' ? 'Cognome' : 'Last Name'} *</Label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="surname"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              placeholder={language === 'it' ? 'Il tuo cognome' : 'Your last name'}
              className={`pl-12 h-12 rounded-xl bg-secondary/30 border-0 ${errors.surname ? 'ring-2 ring-destructive' : ''}`}
              data-testid="premium-surname-input"
            />
          </div>
          {errors.surname && <p className="text-sm text-destructive">{errors.surname}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="email@example.com"
            className={`pl-12 h-12 rounded-xl bg-secondary/30 border-0 ${errors.email ? 'ring-2 ring-destructive' : ''}`}
            data-testid="premium-email-input"
          />
        </div>
        {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">{language === 'it' ? 'Telefono' : 'Phone'} *</Label>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+39 123 456 7890"
            className={`pl-12 h-12 rounded-xl bg-secondary/30 border-0 ${errors.phone ? 'ring-2 ring-destructive' : ''}`}
            data-testid="premium-phone-input"
          />
        </div>
        {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      {/* Age notice */}
      <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-primary">
            {language === 'it' ? 'Requisito di età: ' : 'Age requirement: '}
          </span>
          {language === 'it' 
            ? 'Il servizio è riservato ai maggiorenni (18+ anni).'
            : 'The service is reserved for adults (18+ years).'}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="birth_date">{language === 'it' ? 'Data di Nascita' : 'Date of Birth'} *</Label>
        <div className="relative">
          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="birth_date"
            name="birth_date"
            type="date"
            value={formData.birth_date}
            onChange={handleChange}
            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
            className={`pl-12 h-12 rounded-xl bg-secondary/30 border-0 ${errors.birth_date ? 'ring-2 ring-destructive' : ''}`}
            data-testid="premium-birthdate-input"
          />
        </div>
        {errors.birth_date && <p className="text-sm text-destructive">{errors.birth_date}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="residence">{language === 'it' ? 'Residenza' : 'Residence'} *</Label>
        <div className="relative">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="residence"
            name="residence"
            value={formData.residence}
            onChange={handleChange}
            placeholder={language === 'it' ? 'Via, Città, CAP' : 'Street, City, ZIP'}
            className={`pl-12 h-12 rounded-xl bg-secondary/30 border-0 ${errors.residence ? 'ring-2 ring-destructive' : ''}`}
            data-testid="premium-residence-input"
          />
        </div>
        {errors.residence && <p className="text-sm text-destructive">{errors.residence}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="fiscal_code">{language === 'it' ? 'Codice Fiscale' : 'Fiscal Code'} *</Label>
        <div className="relative">
          <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="fiscal_code"
            name="fiscal_code"
            value={formData.fiscal_code}
            onChange={handleChange}
            placeholder="RSSMRA80A01H501Z"
            className={`pl-12 h-12 rounded-xl bg-secondary/30 border-0 uppercase ${errors.fiscal_code ? 'ring-2 ring-destructive' : ''}`}
            data-testid="premium-fiscalcode-input"
          />
        </div>
        {errors.fiscal_code && <p className="text-sm text-destructive">{errors.fiscal_code}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="profession">{language === 'it' ? 'Professione' : 'Profession'}</Label>
        <div className="relative">
          <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="profession"
            name="profession"
            value={formData.profession}
            onChange={handleChange}
            placeholder={language === 'it' ? 'La tua professione' : 'Your profession'}
            className="pl-12 h-12 rounded-xl bg-secondary/30 border-0"
            data-testid="premium-profession-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="consultation_reason">{language === 'it' ? 'Motivo della Consulenza' : 'Consultation Reason'}</Label>
        <Textarea
          id="consultation_reason"
          name="consultation_reason"
          value={formData.consultation_reason}
          onChange={handleChange}
          placeholder={language === 'it' ? 'Descrivi brevemente il motivo...' : 'Briefly describe the reason...'}
          className="min-h-24 rounded-xl bg-secondary/30 border-0"
          data-testid="premium-reason-input"
        />
      </div>

      <div className="space-y-2">
        <Label>{language === 'it' ? 'Come ci hai conosciuto?' : 'How did you find us?'}</Label>
        <Select onValueChange={(value) => handleSelectChange('how_found_us', value)}>
          <SelectTrigger className="h-12 rounded-xl bg-secondary/30 border-0" data-testid="premium-howfound-select">
            <HelpCircle className="w-5 h-5 text-muted-foreground mr-2" />
            <SelectValue placeholder={language === 'it' ? 'Seleziona...' : 'Select...'} />
          </SelectTrigger>
          <SelectContent>
            {howFoundUsOptions.map((option) => (
              <SelectItem key={option} value={option}>{option}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      <div className="p-4 bg-green-50 border border-green-200 rounded-2xl mb-6">
        <div className="flex items-center gap-3">
          <CheckCircle className="w-6 h-6 text-green-600" />
          <div>
            <p className="font-medium text-green-800">
              {language === 'it' ? 'Quasi fatto!' : 'Almost done!'}
            </p>
            <p className="text-sm text-green-700">
              {language === 'it' ? 'Crea una password per completare la registrazione' : 'Create a password to complete registration'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password *</Label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            placeholder="••••••••"
            className={`pl-12 pr-12 h-12 rounded-xl bg-secondary/30 border-0 ${errors.password ? 'ring-2 ring-destructive' : ''}`}
            data-testid="premium-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">{language === 'it' ? 'Conferma Password' : 'Confirm Password'} *</Label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="••••••••"
            className={`pl-12 h-12 rounded-xl bg-secondary/30 border-0 ${errors.confirmPassword ? 'ring-2 ring-destructive' : ''}`}
            data-testid="premium-confirm-password-input"
          />
        </div>
        {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="register-premium-page">
      <div className="container-narrow">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="bg-white border-0 shadow-large rounded-3xl overflow-hidden">
            <CardHeader className="text-center pb-2 pt-8">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-accent/20 flex items-center justify-center">
                  <Leaf className="w-7 h-7 text-accent" />
                </div>
              </div>
              <CardTitle className="text-2xl font-serif text-primary">
                {language === 'it' ? 'Registrazione Valutazione Completa' : 'Complete Assessment Registration'}
              </CardTitle>
              <CardDescription>
                {language === 'it' 
                  ? 'Compila i tuoi dati per accedere al percorso completo'
                  : 'Fill in your details to access the complete program'}
              </CardDescription>
              
              {/* Progress Steps */}
              <div className="flex justify-center items-center gap-2 mt-6">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      s === step ? 'w-8 bg-accent' : s < step ? 'bg-primary' : 'bg-secondary'
                    }`}
                  />
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {language === 'it' ? `Passo ${step} di 3` : `Step ${step} of 3`}
              </p>
            </CardHeader>
            
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit}>
                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
                {step === 3 && renderStep3()}

                <div className="flex gap-4 mt-8">
                  {step > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 h-12 rounded-full"
                      onClick={handleBack}
                      data-testid="premium-back-btn"
                    >
                      {language === 'it' ? 'Indietro' : 'Back'}
                    </Button>
                  )}
                  
                  {step < 3 ? (
                    <Button
                      type="button"
                      className="flex-1 h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                      onClick={handleNext}
                      data-testid="premium-next-btn"
                    >
                      {language === 'it' ? 'Continua' : 'Continue'}
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className="flex-1 h-12 rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                      disabled={loading}
                      data-testid="premium-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        language === 'it' ? 'Completa Registrazione' : 'Complete Registration'
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPremiumPage;
